import { useRef, useState } from "react";
import { useBank } from "../useBank.js";
import { api, day } from "../../api.js";
import { CURRENCIES, normalizeIban, ibanError, ibanGroups, mfoOf } from "../../../../lib/counterparties/accounts.js";

// Банківські рахунки контрагента: додати, змінити, позначити основний (по одному на валюту), прибрати.
// Основний гривневий підставляється в первинні документи («Реквізити покупця/постачальника»)
const EMPTY = { iban: "", currency: "UAH", bank: "", note: "", isMain: false };

function AccountForm({ initial = EMPTY, submitLabel, onSubmit, onCancel }) {
  const [v, setV] = useState({ ...initial, iban: ibanGroups(initial.iban) });
  const [busy, setBusy] = useState(false), [error, setError] = useState(null), [touched, setTouched] = useState(false);
  const set = (k, x) => setV((o) => ({ ...o, [k]: x }));
  const err = ibanError(v.iban);
  // Банк з довідника НБУ за МФО: підставляємо в порожнє поле або замість попередньої підстановки (IBAN змінили)
  const auto = useRef(""), [touchedBank, setTouchedBank] = useState(false);
  const nbu = useBank(err ? "" : v.iban, (b) => setV((o) => (!o.bank.trim() || o.bank === auto.current ? (auto.current = b.name, { ...o, bank: b.name }) : o)));
  const submit = async (e) => {
    e.preventDefault(); setTouched(true);
    if (err) return;
    setBusy(true); setError(null);
    try { await onSubmit({ ...v, iban: normalizeIban(v.iban), bank: v.bank.trim(), note: v.note.trim() }); }
    catch (x) { setError(x.message); setBusy(false); }
  };
  return (
    <form className="acc-form" onSubmit={submit} aria-label={submitLabel}>
      <div className="acc-grid">
        <label className="field acc-wide"><span>IBAN</span>
          <input className="mono" value={v.iban} maxLength={40} placeholder="UA00 0000 0000 0000 0000 0000 0000 0" autoComplete="off" spellCheck={false}
            aria-invalid={touched && !!err} aria-describedby="acc-iban-hint" onBlur={() => setTouched(true)}
            onChange={(e) => set("iban", ibanGroups(e.target.value.replace(/[^\dA-Za-z\s]/g, "")))} />
          <span id="acc-iban-hint" className={touched && err ? "field-hint error" : "field-hint"}>{touched && err ? err : mfoOf(normalizeIban(v.iban)) ? `МФО ${mfoOf(normalizeIban(v.iban))}${nbu ? ` · ${nbu.name} (довідник НБУ)` : ""}` : "29 знаків: UA і 27 цифр"}</span></label>
        <label className="field"><span>Валюта</span>
          <select value={v.currency} onChange={(e) => set("currency", e.target.value)}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></label>
        <label className="field"><span>Банк</span><input value={v.bank} maxLength={200} placeholder="підставиться з довідника НБУ за IBAN" onChange={(e) => { setTouchedBank(true); set("bank", e.target.value); }} />
          {nbu && touchedBank && v.bank !== nbu.name && <span className="field-hint">За НБУ: {nbu.name} <button type="button" className="linkish" onClick={() => set("bank", nbu.name)}>підставити</button></span>}</label>
        <label className="field acc-wide"><span>Примітка (необов'язково)</span><input value={v.note} maxLength={300} placeholder="Напр. для оплати за договором № 12" onChange={(e) => set("note", e.target.value)} /></label>
        <label className="check acc-wide"><input type="checkbox" checked={v.isMain} onChange={(e) => set("isMain", e.target.checked)} /> Основний рахунок у {v.currency} — підставляти в документи</label>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="acc-actions">
        <button type="submit" className="btn btn-sm" disabled={busy}>{busy ? "Зберігаємо…" : submitLabel}</button>
        {onCancel && <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>Скасувати</button>}
      </div>
      <p className="muted small">Банк визначається за МФО в IBAN з довідника банків НБУ; назву можна змінити.</p>
    </form>
  );
}

function Copy({ text }) {
  const [done, setDone] = useState(false);
  const go = async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* немає доступу до буфера */ } };
  return <button type="button" className="copy-btn no-print" onClick={go} aria-label="Копіювати IBAN">{done ? "скопійовано" : "⧉"}</button>;
}

// Список після змін — з відповіді сервера: onChange віддає його досьє (шапка, огляд) без повторного запиту
export default function AccountsTab({ companyId, tin, accounts = [], canEdit, onChange }) {
  const [mode, setMode] = useState(null); // null | "add" | id рахунку, що редагуємо
  const [msg, setMsg] = useState(null);
  const url = `/api/companies/${companyId}/partners/${tin}/accounts`;
  const done = (r, text) => { onChange?.(r.accounts); setMode(null); setMsg({ ok: text }); };
  const add = async (v) => done(await api(url, { method: "POST", body: v }), "Рахунок додано");
  const edit = (id) => async (v) => done(await api(`${url}/${id}`, { method: "PATCH", body: v }), "Рахунок змінено");
  const act = async (fn, text) => {
    setMsg(null);
    try { done(await fn(), text); } catch (e) { setMsg({ error: e.message }); }
  };
  const makeMain = (a) => act(() => api(`${url}/${a.id}`, { method: "PATCH", body: { isMain: true } }), `Основний рахунок у ${a.currency} змінено`);
  const remove = (a) => {
    if (!confirm(`Прибрати рахунок ${ibanGroups(a.iban)}?`)) return;
    act(() => api(`${url}/${a.id}`, { method: "DELETE" }), "Рахунок прибрано");
  };

  return (
    <section className="card" aria-labelledby="acc-h">
      <div className="row">
        <h3 id="acc-h" className="block-title">Банківські рахунки{accounts.length ? ` (${accounts.length})` : ""}</h3>
        {canEdit && mode !== "add" && <button type="button" className="btn btn-sm no-print" onClick={() => { setMode("add"); setMsg(null); }}>+ Додати рахунок</button>}
      </div>
      <p className="muted small">У реєстрах банківських реквізитів немає — їх вносите ви. Основний рахунок у гривні підставляється в договори, рахунки й накладні з цим контрагентом.</p>
      {mode === "add" && <AccountForm initial={{ ...EMPTY, isMain: !accounts.some((a) => a.currency === "UAH") }} submitLabel="Додати рахунок" onSubmit={add} onCancel={() => setMode(null)} />}
      {msg && <p className={msg.error ? "error" : "muted small"} role={msg.error ? "alert" : "status"}>{msg.error || msg.ok}</p>}
      {!accounts.length ? (mode !== "add" && <p className="muted">Рахунків ще немає.{canEdit ? " Додайте IBAN з рахунку на оплату чи договору." : ""}</p>) : (
        <ul className="acc-list">
          {accounts.map((a) => mode === a.id ? (
            <li key={a.id}><AccountForm initial={a} submitLabel="Зберегти" onSubmit={edit(a.id)} onCancel={() => setMode(null)} /></li>
          ) : (
            <li key={a.id} className="acc-item">
              <div>
                <p><b className="mono">{ibanGroups(a.iban)}</b><Copy text={a.iban} /> <span className="badge badge-idle">{a.currency}</span>
                  {a.isMain && <span className="badge badge-ok">основний</span>}</p>
                <p className="small">{a.bank || <span className="muted">банк не вказано</span>} <span className="muted">· МФО {mfoOf(a.iban)}</span></p>
                {a.note && <p className="muted small">{a.note}</p>}
                <p className="muted small">Додано {day(a.addedAt)}</p>
              </div>
              {canEdit && <div className="acc-actions no-print">
                {!a.isMain && <button type="button" className="btn btn-sm btn-ghost" onClick={() => makeMain(a)}>Зробити основним</button>}
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setMode(a.id); setMsg(null); }}>Змінити</button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => remove(a)}>Прибрати</button>
              </div>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
